/**
 * @file `@majiang/core` 公共 API 契约。
 *
 * 本文件是 M1 的**冻结面**：M1-T3（实现）与 M1-T4（单测）都只依赖这里的签名。
 * 所有函数体当前均为 `notImplemented(...)`，目的是让类型检查先跑通、
 * 让测试可以先按契约写出来（验证驱动开发）。
 *
 * ## 三条不可协商的设计约束
 *
 * 1. **纯函数**：所有导出函数都是 `(输入) => 输出`，不读全局、不写全局、无副作用。
 * 2. **无平台依赖**：不出现 `Date.now()` / `Math.random()` / `crypto` / `fetch` / `console`。
 *    时间与随机性一律由调用方**注入**（`now` 参数、`seed` 参数）。
 *    这是「同一份 core 同时跑在浏览器单机模式和 Cloudflare Workers 上」的硬前提，
 *    也是牌局可完整回放、可对拍的前提。见 ADR-003。
 * 3. **信息隔离内建**：脱敏不是宿主的自觉，而是 core 的产物 ——
 *    宿主只能通过 `projectStateForSeat()` 和 `applyAction()` 返回的
 *    带 `audience` 的事件与外界通信。见 `docs/architecture/00-overview.md` §6。
 */

import type {
  Action,
  ApplyResult,
  GameState,
  Meld,
  PlayerId,
  SeatIndex,
  Settlement,
  Suit,
  Tile,
  TileId,
  TileKind,
  WinKind,
} from './types';
import type {
  RuleConfig,
  RuleConfigIssue,
  RuleConfigPatch,
  RuleSnapshot,
} from './config';
import type { SeatView } from './view';
import { kindOfTileId as _kindOfTileId, tileOf as _tileOf, idsOfKind as _idsOfKind, createOrderedDeck as _createOrderedDeck } from './tiles';
import { defaultRuleConfig as _defaultRuleConfig, normalizeRuleConfig as _normalizeRuleConfig, canonicalizeRuleConfig as _canonicalizeRuleConfig, hashRuleConfig as _hashRuleConfig, createRuleSnapshot as _createRuleSnapshot } from './config-pipeline';
import { serializeState as _serializeState, deserializeState as _deserializeState } from './serialize';
import { assertStateInvariants as _assertStateInvariants } from './invariants';
import { checkWin as _checkWin } from './check-win';
import { createGame as _createGame, applyAction as _applyAction, getLegalActions as _getLegalActions } from './game';
import { evaluateTing as _evaluateTing } from './ting';
import { calculateScore as _calculateScore } from './settlement';
import { projectStateForSeat as _projectStateForSeat } from './projection';

/* ═══════════════════════ 类型再导出 ═══════════════════════ */

export type {
  Suit,
  Rank,
  TileKind,
  TileId,
  Tile,
  SeatIndex,
  PlayerId,
  MeldType,
  Meld,
  TingCandidate,
  WinKind,
  WinRecord,
  PlayerState,
  GamePhase,
  SwapDirection,
  ClaimActionType,
  ClaimOption,
  PendingClaims,
  LastDiscard,
  RngState,
  Seats,
  GameState,
  ScoreTransfer,
  Settlement,
  ActionType,
  ActionBase,
  DeclareVoidAction,
  SwapSelectAction,
  DrawAction,
  DiscardAction,
  PengAction,
  GangAction,
  HuAction,
  PassAction,
  TimeoutAction,
  Action,
  Audience,
  GameEventType,
  GameEvent,
  ApplyResult,
} from './types';

export { SUITS, RANKS, SEATS, TOTAL_TILES, TILE_KIND_COUNT } from './types';

export type {
  FanKey,
  FanRule,
  FanTable,
  ScoringConfig,
  GangScoreConfig,
  MingGangPayer,
  GangConfig,
  QiangGangConfig,
  HuConfig,
  DiHuMode,
  XuezhanConfig,
  ChaDaJiaoConfig,
  GangRefundMode,
  WallConfig,
  ShuffleSource,
  TimeoutConfig,
  AIConfig,
  AIDifficulty,
  AIWeightOverrides,
  VariantConfig,
  JokerVariant,
  XiPaiVariant,
  KanShuFanVariant,
  HuanSanZhangVariant,
  XueLiuChengHeVariant,
  NoQueModeVariant,
  RuleConfig,
  SichuanRuleConfig,
  RuleConfigPatch,
  DeepPartial,
  RuleSnapshot,
  RuleConfigIssue,
  RuleValidationCode,
} from './config';

export {
  RULE_SCHEMA_VERSION,
  RULE_CONFIG_VERSION,
  FAN_KEYS,
  CHENGDU_STANDARD,
  M1_LOCKED_VALUES,
  M1_ENFORCED_FAN_CAP,
} from './config';

export type { OpponentView, SelfView, SelfActionHint, SeatView, ForbiddenPublicField } from './view';

export { FORBIDDEN_PUBLIC_FIELDS } from './view';

export type { RejectReason } from './errors';

export {
  NotImplementedError,
  InvariantViolationError,
  RuleConfigError,
} from './errors';

/* ═══════════════════════ 牌与 ID 映射 ═══════════════════════ */

/**
 * 由牌实例 id 推导牌面种类。
 *
 * 映射规则（**公开且确定**）：`kindIndex = floor(id / 4)`，
 * `kindIndex` 按 `SUITS × RANKS` 的字典序展开（wan1..wan9, tong1..tong9, tiao1..tiao9）。
 *
 * @param id 牌实例 id，必须满足 `0 <= id < 108`
 * @returns 对应牌面
 * @throws {InvariantViolationError} id 越界
 */
export function kindOfTileId(id: TileId): TileKind {
  return _kindOfTileId(id);
}

/**
 * 由牌实例 id 构造富信息 `Tile`。仅供 AI 评估与 UI 渲染使用；
 * 状态与协议层一律直接传 id。
 *
 * @param id 牌实例 id
 */
export function tileOf(id: TileId): Tile {
  return _tileOf(id);
}

/**
 * 取某牌面对应的 4 个实例 id。
 *
 * @param kind 牌面种类
 * @returns 长度恒为 4 的升序 id 数组
 */
export function idsOfKind(kind: TileKind): readonly TileId[] {
  return _idsOfKind(kind);
}

/**
 * 生成一副未洗的标准牌（id 0..107 升序）。
 *
 * 洗牌**不在**这里做 —— 洗牌需要随机源，属于 `createGame()` 的职责，
 * 且必须走注入的确定性 PRNG。
 */
export function createOrderedDeck(): readonly TileId[] {
  return _createOrderedDeck();
}

/* ═══════════════════════ 规则配置与快照 ═══════════════════════ */

/**
 * 返回内置默认规则配置（成都标准）的**独立深拷贝**。
 *
 * 与直接引用 `CHENGDU_STANDARD` 的区别：后者是共享常量，调用方改一下就污染全局。
 * 这个函数每次返回全新对象，改坏了也只坏自己那份。
 *
 * 默认取值见 `config.ts` 的 {@link CHENGDU_STANDARD}，
 * 权威来源是 `design/gdd/07-rule-config-schema.md` §3。
 */
export function defaultRuleConfig(): RuleConfig {
  return _defaultRuleConfig();
}

/**
 * 规范化规则配置：深拷贝 → 以 `CHENGDU_STANDARD` 补默认值 → 校验 → 版本迁移。
 *
 * 必须执行 `design/gdd/07-rule-config-schema.md` §5 的 **V-1 ~ V-16** 全部校验，
 * 错误码取 `RuleValidationCode`。其中 V-8 ~ V-16 是「未实现护栏」，
 * 期望值已固化在 `M1_LOCKED_VALUES`，实现时直接对表即可。
 *
 * 几条容易漏的：
 * - **V-8**：任一 `variant.*.enabled === true` 必须报 `error` 并带上具体变体名，
 *   不许静默忽略（GDD 原则 P5）；
 * - **未知字段一律忽略而非报错**（GDD 原则 P4，向前兼容，便于灰度发新规则）；
 * - `timeout.reconnectGraceMs` 过大时报 `warning`——它会按比例抬高
 *   Durable Object 的驻留时长与免费额度消耗。
 *
 * @param patch 用户提供的差异配置
 * @returns 规范化后的完整配置与问题清单；存在 `severity: 'error'` 时由
 *          `createRuleSnapshot()` 转抛 {@link RuleConfigError}
 */
export function normalizeRuleConfig(patch: RuleConfigPatch): {
  readonly config: RuleConfig;
  readonly issues: readonly RuleConfigIssue[];
} {
  return _normalizeRuleConfig(patch);
}

/**
 * 规范序列化：递归按键的字典序重排、剔除 `undefined`、数字统一为最短往返表示。
 *
 * 契约：**语义相同的两份配置必须产出逐字节相同的字符串**。
 * 这是 `ruleHash` 可比对的唯一依据，M1-T4 会用打乱键序的等价配置做对拍测试。
 *
 * @param config 已规范化的配置
 */
export function canonicalizeRuleConfig(config: RuleConfig): string {
  return _canonicalizeRuleConfig(config);
}

/**
 * 对规范 JSON 求 64 位 FNV-1a 哈希，返回 16 位小写十六进制。
 *
 * 选非加密哈希是刻意的：它的用途是**检测双方规则口径是否一致**，
 * 而不是防篡改 —— 防篡改由 server-authoritative 架构本身保证。
 * 纯 TS 实现也避免了 core 依赖 `crypto`（Workers 与浏览器的 API 形态并不一致）。
 *
 * @param canonicalJson `canonicalizeRuleConfig()` 的输出
 */
export function hashRuleConfig(canonicalJson: string): string {
  return _hashRuleConfig(canonicalJson);
}

/**
 * 创建房间级不可变规则快照。这是 ADR-005 五步管线的唯一入口：
 * 深拷贝 → 规范化 → 规范序列化 → 哈希 → 递归冻结。
 *
 * 调用时机：**房间创建的那一刻，且仅此一次**。房间存续期间不得重新生成。
 *
 * @param patch 用户在创建房间时选择的规则差异项
 * @returns 深冻结的规则快照
 * @throws {RuleConfigError} 规范化阶段产生了 `error` 级问题
 */
export function createRuleSnapshot(patch: RuleConfigPatch): RuleSnapshot {
  return _createRuleSnapshot(patch);
}

/* ═══════════════════════ 牌局生命周期 ═══════════════════════ */

/** 一个座位的入座信息。 */
export interface SeatSetup {
  readonly playerId: PlayerId;
  readonly isRobot: boolean;
}

/** `createGame()` 的入参。 */
export interface CreateGameOptions {
  readonly gameId: string;
  /**
   * 32 位无符号随机种子。**由宿主注入**：
   * 服务端用 `crypto.getRandomValues()`，单机用本地熵。
   * core 自己不产生随机性，以保证牌局可完整回放。
   */
  readonly seed: number;
  readonly rules: RuleSnapshot;
  readonly seats: readonly [SeatSetup, SeatSetup, SeatSetup, SeatSetup];
  readonly dealer: SeatIndex;
  /** 当前时间（epoch ms），由宿主注入。core 内禁止调用 `Date.now()`。 */
  readonly now: number;
}

/**
 * 创建并发牌，推进到第一个需要玩家输入的阶段（换三张或定缺）。
 *
 * @returns 初始权威状态 + 应下发的事件（发牌事件的 `audience` 已按座位裁剪）
 */
export function createGame(options: CreateGameOptions): ApplyResult {
  return _createGame(options);
}

/** `applyAction()` 的宿主上下文。 */
export interface EngineContext {
  /** 当前时间（epoch ms）。用于计算超时截止点与事件时间戳。 */
  readonly now: number;
}

/**
 * 应用一个动作，产出新状态与事件序列。**引擎的唯一状态推进入口。**
 *
 * 行为契约：
 * - 输入状态**不被修改**（纯函数）；
 * - 动作非法时**不抛异常**，返回 `state` 原样 + 一条 `action_rejected` 事件
 *   （`audience` 为提交者本人），原因见 {@link RejectReason}；
 * - 合法时 `state.seq` 恰好 +1；
 * - 事件序列的 `audience` 已完成脱敏，宿主直接按 scope 路由即可；
 * - 一次调用可能连带推进多步（如碰牌后自动进入出牌、四家 pass 后自动摸牌）。
 *
 * @param state 当前权威状态
 * @param action 待应用的动作
 * @param ctx 宿主上下文
 */
export function applyAction(
  state: GameState,
  action: Action,
  ctx: EngineContext,
): ApplyResult {
  return _applyAction(state, action, ctx);
}

/**
 * 枚举某座位当前的全部合法动作。
 *
 * 三个用途：① 服务端校验客户端提交；② AI 的候选动作空间；③ UI 按钮可用态。
 * 三者共用同一函数，从根上杜绝「客户端认为能碰、服务端认为不能」的口径分裂。
 *
 * @param state 当前权威状态
 * @param seat 目标座位
 */
export function getLegalActions(state: GameState, seat: SeatIndex): readonly Action[] {
  return _getLegalActions(state, seat);
}

/* ═══════════════════════ 胡牌判定与计分 ═══════════════════════ */

/** 胡牌判定的入参。刻意做成**不依赖 `GameState`** 的纯输入，便于单测穷举。 */
export interface WinCheckInput {
  /** 门清手牌（不含副露），不含 `winningTile`。 */
  readonly hand: readonly TileId[];
  readonly melds: readonly Meld[];
  /** 待判定是否能成胡的那张牌。 */
  readonly winningTile: TileId;
  /** 胡牌方式，影响杠上花/抢杠等番型。 */
  readonly winKind: WinKind;
  /** 定缺花色。持有定缺花色的牌一律不能胡。 */
  readonly voidSuit: Suit | null;
  readonly rules: RuleSnapshot;
  /** 牌墙是否已摸完（影响海底捞月等番型，若 GDD 启用）。 */
  readonly isLastTile: boolean;
  /** 该玩家已有的「根」数量等预计算上下文，可为空由函数自行推导。 */
  readonly gen: number | null;
}

/** 胡牌判定结果。 */
export interface WinCheckResult {
  readonly isWin: boolean;
  /** 命中的番型标识。 */
  readonly fanTags: readonly string[];
  /** 封顶前番数。 */
  readonly rawFan: number;
  /** 封顶后番数。 */
  readonly cappedFan: number;
  /** 不能胡时的原因（如 `'void_suit_held'` / `'no_valid_decomposition'`）。 */
  readonly rejectReason: string | null;
}

/**
 * 判定是否成胡并计算番型。纯计算，不触碰牌局状态。
 *
 * @param input 判定输入
 */
export function checkWin(input: WinCheckInput): WinCheckResult {
  return _checkWin(input);
}

/**
 * 计算终局结算：胡牌分、杠分、查大叫、查花猪、退税、呼叫转移。
 *
 * 契约：返回的 `transfers` 聚合后必须与 `netScores` 完全一致，
 * 且四家 `netScores` 之和恒为 0（零和校验，M1-T4 强制断言）。
 *
 * @param state 已进入 `settlement` 阶段的权威状态
 */
export function calculateScore(state: GameState): Settlement {
  return _calculateScore(state);
}

/**
 * 计算听牌信息（是否成叫、听哪些牌、各自番数）。
 * 查大叫与「听牌提示」共用此函数。
 *
 * @param state 当前权威状态
 * @param seat 目标座位
 */
export function evaluateTing(
  state: GameState,
  seat: SeatIndex,
): { readonly isTing: boolean; readonly candidates: readonly TileKind[] } {
  return _evaluateTing(state, seat);
}

/* ═══════════════════════ 信息隔离 ═══════════════════════ */

/**
 * 把权威状态投影成某座位可见的视图。**下行快照的唯一生产者。**
 *
 * 宿主（Durable Object / 单机 runtime）除了本函数的返回值和
 * `applyAction()` 的事件，不得以任何其他方式把 `GameState` 的内容送出边界。
 *
 * @param state 权威状态
 * @param seat 观察者座位
 */
export function projectStateForSeat(state: GameState, seat: SeatIndex): SeatView {
  return _projectStateForSeat(state, seat);
}

/* ═══════════════════════ 持久化与不变量 ═══════════════════════ */

/**
 * 把权威状态序列化成紧凑字符串，供 Durable Object 存储 checkpoint。
 *
 * 使用规范序列化（键序稳定），使得「状态未变则字符串未变」，
 * 宿主可据此跳过无谓的存储写入 —— 这直接关系到免费额度里
 * 「DO SQLite 每日 10 万行写」这条最紧的约束。见零成本部署方案 §4.2。
 *
 * @param state 权威状态
 */
export function serializeState(state: GameState): string {
  return _serializeState(state);
}

/**
 * 反序列化权威状态。
 *
 * @param json `serializeState()` 的输出
 * @throws {InvariantViolationError} 数据损坏或 Schema 版本不兼容
 */
export function deserializeState(json: string): GameState {
  return _deserializeState(json);
}

/**
 * 断言状态满足所有内部不变量。开发期与测试中每步调用；生产环境按需开启。
 *
 * 检查项：
 * - **实体守恒**：牌墙 + 四家手牌 + 副露 + 弃牌 = 恰好 108 个互异 id；
 * - 每个牌面恰好 4 张；
 * - `seq` 单调；`phase` 与 `pendingClaims` / `deadlineAt` 的组合合法；
 * - 已胡牌座位不再持有行动权；
 * - `winnersInOrder` 长度 ≤ 3 且与各 `PlayerState.hasWon` 一致。
 *
 * @param state 待校验状态
 * @throws {InvariantViolationError} 任一不变量被破坏
 */
export function assertStateInvariants(state: GameState): void {
  return _assertStateInvariants(state);
}

/* ═══════════════════════ AI 决策（M2） ═══════════════════════ */

export type {
  TileKey,
  TileCounts,
  Difficulty,
  AIPhase,
  GangType,
  GangCandidate,
  AIMeld,
  AIOpponentInfo,
  AIObservation,
  AIAction,
  AIDecision,
  SeenScope,
} from './ai/types';

export type {
  DifficultyPreset,
  ShantenMode,
  UkeireMode,
  QueMode,
  PengMode,
  GangMode,
} from './ai/params';

export {
  shantenStandard,
  shantenQiDui,
  shantenSichuan,
  shantenForMode,
  copyCounts,
} from './ai/shanten';

export { ukeire } from './ai/ukeire';
export { evaluateQue, pickQue } from './ai/que';

export {
  tileRiskProfile,
  threatLevel,
  dangerCost,
  effCost,
  yakuCost,
  flexCost,
  dynamicSafeWeight,
  discardCost,
  makeValuationContext,
  pickWithNoise,
  getLegalDiscards,
  twoStepAdvantage,
} from './ai/discard';

export {
  shouldHu,
  shouldPeng,
  isQiDuiOriented,
  isDuiDuiOriented,
  gangValue,
  shouldGang,
  estimateDrawProbability,
  estimateQiangGangRisk,
  listSelfGangs,
  canMingGang,
  canPeng,
} from './ai/claims';

export {
  DIFFICULTY_PRESETS,
  W_COUNT,
  W_TRIPLET,
  W_PAIR,
  W_CONN,
  W_GAP,
  W_ISOLATED,
  GEN_FAN_VALUE,
  GANG_SCORE,
} from './ai/params';

export { buildSeenCounts, decide, computeThinkDelayMs } from './ai';

/* ═══════════════════════ 版本信息 ═══════════════════════ */

/** core 包的契约版本。协议握手时与服务端比对，不一致则拒绝开局。 */
export const CORE_CONTRACT_VERSION = '0.1.0' as const;
